import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import sessionManager from '../utils/sessionManager';
import API_CONFIG from '../config';
import './UserDropdown.css';

const UserDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Get user info from session
    const session = sessionManager.getUserInfo();
    setUserInfo(session);

    // Fetch user profile data to get first name and last name
    const fetchUserProfile = async () => {
      if (session?.authToken) {
        try {
          const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_USER_PREFERENCES}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.authToken}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.profile) {
              setUserProfile(data.profile);
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };

    fetchUserProfile();

    // Fetch unread messages count
    const fetchUnreadCount = async () => {
      if (session?.authToken) {
        try {
          const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_UNREAD_COUNT}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.authToken}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setUnreadCount(data.unread_count || 0);
            }
          }
        } catch (error) {
          console.error('Error fetching unread count:', error);
        }
      }
    };

    fetchUnreadCount();

    // Poll for unread count every 30 seconds
    const unreadInterval = setInterval(fetchUnreadCount, 30000);

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(unreadInterval);
    };
  }, []);

  const handleChatWithAI = () => {
    setIsOpen(false);
    navigate('/chat');
  };

  const handleProfile = () => {
    setIsOpen(false);
    // Navigate to dedicated profile page
    navigate('/profile');
  };

  const handleSignOut = () => {
    setIsOpen(false);
    sessionManager.clearSession();
    window.location.reload(); // Reload to reset authentication state
  };

  if (!userInfo) return null;

  // Get user display info
  const getDisplayName = () => {
    // If we have profile data with first name or last name, use that
    if (userProfile) {
      const firstName = userProfile.firstName || '';
      const lastName = userProfile.lastName || '';
      
      if (firstName || lastName) {
        // Show first name + first letter of last name
        if (firstName && lastName) {
          return `${firstName} ${lastName.charAt(0).toUpperCase()}.`;
        } else if (firstName) {
          return firstName;
        } else if (lastName) {
          return lastName;
        }
      }
    }
    
    // Fallback to formatted phone number
    if (userInfo?.phoneNumber) {
      const phone = userInfo.phoneNumber.replace(/\D/g, '');
      if (phone.length === 10) {
        return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
      } else if (phone.length === 11 && phone.startsWith('1')) {
        return `+1 (${phone.slice(1, 4)}) ${phone.slice(4, 7)}-${phone.slice(7)}`;
      }
      return userInfo.phoneNumber;
    }
    return 'User';
  };

  const getRoleIcon = () => {
    const role = userProfile?.role || userInfo.userRole;
    switch (role) {
      case 'tenant': return '🏠';
      case 'landlord': return '🏢';
      case 'agent': return '👔';
      case 'property_manager': return '📋';
      default: return '👤';
    }
  };

  const getRoleDisplayName = () => {
    const role = userProfile?.role || userInfo.userRole;
    switch (role) {
      case 'tenant': return 'Tenant';
      case 'landlord': return 'Landlord';
      case 'agent': return 'Real Estate Agent';
      case 'property_manager': return 'Property Manager';
      default: return 'User';
    }
  };

  return (
    <div className="user-dropdown" ref={dropdownRef}>
      <button 
        className="user-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">
          <span className="user-icon">{getRoleIcon()}</span>
        </div>
        <div className="user-info">
          <span className="user-name">{getDisplayName()}</span>
          <span className="user-role">{getRoleDisplayName()}</span>
        </div>
        
        {/* Bell icon with unread count */}
        {unreadCount > 0 && (
          <div className="notification-bell" title={`You have ${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`}>
            <span className="bell-icon">🔔</span>
            <span className="unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          </div>
        )}
        
        <svg className={`dropdown-arrow ${isOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 12 12">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && (
        <div className="user-dropdown-menu">
          <button className="dropdown-item" onClick={handleChatWithAI}>
            <span className="item-icon">💬</span>
            <span>Chat with AI</span>
          </button>
          <button className="dropdown-item" onClick={() => { setIsOpen(false); navigate('/map'); }}>
            <span className="item-icon">🗺️</span>
            <span>Map View</span>
          </button>
          <button className="dropdown-item" onClick={() => window.location.href = '/messages'}>
            <span className="item-icon">📨</span>
            <span>Messages</span>
            {unreadCount > 0 && (
              <span className="menu-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
          <button className="dropdown-item" onClick={handleProfile}>
            <span className="item-icon">👤</span>
            <span>Profile</span>
          </button>
          <div className="dropdown-divider"></div>
          <button className="dropdown-item sign-out" onClick={handleSignOut}>
            <span className="item-icon">🚪</span>
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDropdown; 